import { useCallback } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { parseVoiceCommands } from "../utils/voiceCommands";

/** Impostare a `true` per mostrare di nuovo i pulsanti microfono nel Diario consegne. */
export const VOICE_DICTATION_ENABLED = false;

const TURNO_LABELS = {
  mattina: "Mattina",
  pomeriggio: "Pomeriggio",
  notte: "Notte",
};

/**
 * Pulsante microfono per dettatura vocale su un campo testuale.
 *
 * Props:
 *  - value: string — valore corrente del campo
 *  - onChange: (newValue: string) => void — aggiorna il testo del campo
 *  - onTurnoChange: (turno: string) => void — (opzionale) aggiorna il turno via comando vocale
 *  - onDataChange: (data: string) => void — (opzionale) aggiorna la data via comando vocale
 *  - lang: string — lingua (default "it-IT")
 *  - showInterim: bool — mostra testo provvisorio sotto il campo (default true)
 *  - className: string — classi aggiuntive per il bottone
 *
 * Comandi vocali supportati (richiedono la parola trigger "imposta"):
 *  "imposta mattina / pomeriggio / notte"
 *  "imposta oggi / ieri / domani"
 *  "imposta <giorno> <mese>"  es. "imposta diciassette maggio"
 */
export function VoiceDictateButton(props) {
  if (!VOICE_DICTATION_ENABLED) return null;
  return <VoiceDictateButtonInner {...props} />;
}

function VoiceDictateButtonInner({
  value = "",
  onChange,
  onTurnoChange,
  onDataChange,
  lang = "it-IT",
  showInterim = true,
  className = "",
}) {
  const handleFinalText = useCallback(
    (chunk) => {
      const trimmedChunk = chunk.trim();
      if (!trimmedChunk) return;

      const hasCommands = onTurnoChange || onDataChange;

      if (hasCommands) {
        const result = parseVoiceCommands(trimmedChunk, {
          onTurno: onTurnoChange
            ? (turno) => {
                onTurnoChange(turno);
                toast.success(`Turno: ${TURNO_LABELS[turno] ?? turno}`, { duration: 2500 });
              }
            : undefined,
          onData: onDataChange
            ? (data) => {
                onDataChange(data);
                const label = new Date(`${data}T12:00:00`).toLocaleDateString("it-IT", {
                  weekday: "long", day: "numeric", month: "long",
                });
                toast.success(`Data: ${label}`, { duration: 2500 });
              }
            : undefined,
        });

        // null → era un comando, non appende nulla
        if (result === null) return;

        // result non null → testo normale, lo appendiamo
        appendText(result);
      } else {
        appendText(trimmedChunk);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange, onTurnoChange, onDataChange]
  );

  const appendText = useCallback(
    (text) => {
      const current = value ?? "";
      const needsSpace = current.length > 0 && !/[\s\-—]$/.test(current);
      const separator = needsSpace ? " " : "";
      onChange(current + separator + text);
    },
    [value, onChange]
  );

  const { isSupported, isListening, interimText, toggle } = useSpeechToText({
    lang,
    onFinalText: handleFinalText,
  });

  const handleClick = () => {
    if (!isSupported) {
      toast.error("Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.");
      return;
    }
    if (!isListening) {
      toast.info("In ascolto… parla ora", { duration: 2000 });
    }
    toggle();
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="outline"
        size="icon"
        title={
          !isSupported
            ? "Riconoscimento vocale non supportato da questo browser"
            : isListening
            ? "Ferma registrazione"
            : "Detta vocalmente"
        }
        onClick={handleClick}
        className={`h-9 w-9 shrink-0 transition-colors ${
          isListening
            ? "border-red-400 bg-red-50 text-red-600 animate-pulse hover:bg-red-100"
            : "hover:border-emerald-400 hover:text-emerald-700"
        } ${className}`}
      >
        {isListening ? (
          <MicOff className="w-4 h-4" />
        ) : (
          <Mic className="w-4 h-4" />
        )}
      </Button>

      {showInterim && isListening && interimText && (
        <p className="text-xs text-slate-400 italic px-1 max-w-xs truncate">
          {interimText}
        </p>
      )}
    </div>
  );
}

export default VoiceDictateButton;
